import React, { FC } from "react";
import { Col, Row } from "react-bootstrap";

import {
    DailySummaryAlignmentType,
    WeeklySummaryAlignmentType,
} from "../../../../DTO";
import { TrafficLight } from "../../../../components";

type AlignmentSectionProps = {
    title: string;
    alignment: WeeklySummaryAlignmentType | DailySummaryAlignmentType;
};

export const AlignmentSection: FC<AlignmentSectionProps> = ({
    title,
    alignment,
}) => {
    const renderActivityMetrics = (metrics: Record<string, any>) => {
        if (
            !metrics ||
            metrics.target_minutes == null ||
            metrics.target_steps == null
        ) {
            return null;
        }

        return (
            <div className="summary-key-row mt-2">
                <span>Target</span>
                <span>
                    {metrics.target_minutes} min or{" "}
                    {new Intl.NumberFormat("en-US").format(
                        Number(metrics.target_steps),
                    )}{" "}
                    steps
                </span>
            </div>
        );
    };

    return (
        <div className="summary-tile">
            <div className="summary-tile-label">{title}</div>

            <Row className="g-2 mb-2">
                <Col md={4}>
                    <div className="summary-mini-tile">
                        <div className="small text-muted">Overall</div>
                        <TrafficLight
                            status={alignment.overall.overall_status}
                            compact
                            label="Overall rating"
                        />
                    </div>
                </Col>
                <Col md={8}>
                    <div className="summary-mini-tile">
                        <div className="small text-muted">Category ratings</div>
                        <div className="small summary-readable-counts">
                            <span>
                                On track:{" "}
                                {alignment.overall.traffic_light_counts.green}
                            </span>
                            <span>
                                Needs attention:{" "}
                                {alignment.overall.traffic_light_counts.amber}
                            </span>
                            <span>
                                Off track:{" "}
                                {alignment.overall.traffic_light_counts.red}
                            </span>
                            <span>
                                Not enough data:{" "}
                                {alignment.overall.traffic_light_counts.no_data}
                            </span>
                        </div>
                    </div>
                </Col>
            </Row>

            {alignment.suggested_focus?.message && (
                <div className="summary-focus-banner mb-2">
                    <div className="fw-semibold">
                        {alignment.suggested_focus.title}
                    </div>
                    <div className="small">
                        {alignment.suggested_focus.message}
                    </div>
                    <div className="small text-muted">
                        {alignment.suggested_focus.recommended_action}
                    </div>
                </div>
            )}

            <Row className="g-2">
                {alignment.cards.map((card) => (
                    <Col md={6} key={card.key}>
                        <div className="summary-card-box">
                            <div className="d-flex justify-content-between align-items-center gap-2">
                                <div className="fw-semibold">{card.title}</div>
                                <TrafficLight
                                    status={card.status}
                                    compact
                                    label=""
                                />
                            </div>
                            <div className="small my-1">
                                {card.status === "no_data" ? (
                                    "No score — not enough recorded data"
                                ) : (
                                    <>
                                        Score:{" "}
                                        <span className="fw-semibold">
                                            {card.score}/100
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="small text-muted">
                                {card.reason}
                            </div>
                            {card.key === "physical_activity" &&
                                renderActivityMetrics(card.metrics)}
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
};
